<script setup>

    import moment from 'moment'

    import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useAuthStore, useEngagementStore, useStaffStore, useViewStore, useNotificationStore } from '@/Stores'

    import { util } from '@/Helpers'

    import Modal from '@/Components/App/Modal.vue'
    import Announcement from '@/Components/App/Announcement.vue'
    import LatteStyledContainer from '@/Components/App/LatteStyledContainer.vue'
    import LatteEngagementOpportunitiesTable from '@/Components/LatteEngagementOpportunitiesTable.vue'
    import LatteEngagementHistoryTable from '@/Components/LatteEngagementHistoryTable.vue'
    import LatteEngagementCreateEditForm from '@/Components/LatteEngagementCreateEditForm.vue'
    import QuickSearch from '@/Components/App/QuickSearch.vue'

    import LwxSwitch from '@/Components/Forms/LwxSwitch.vue'
import { UserPlusIcon } from '@heroicons/vue/20/solid'

    const viewStore = useViewStore()
    const authStore = useAuthStore()
    const staffStore = useStaffStore()
    const notificationStore = useNotificationStore()
    const engagementStore = useEngagementStore()
    const { selectedGiftRequest, selectedGiftRequestLoading, scheduledEngagementData, showAllScheduledRequests } = storeToRefs(engagementStore)
    const { user } = storeToRefs(authStore)
    const { lastNavSelected } = storeToRefs(viewStore)

    function announcementClick() {
        util.scrollToElement('latte-engagement-opportunities')
    }

    const navItems = computed(() => {
        return [
            {
                id: 'cta-engagement-add-manually',
                label: 'Add Manually',
                // status: 0
            },
            {
                id: 'latte-engagement-opportunities',
                label: 'New Opportunities',
                // status: 0
            },
            {
                id: 'latte-engagement-history',
                label: 'Scheduled & Sent',
                // status: 0
            }
        ];
    })

    const daysRemaining = computed(() => {
        const today = moment()
        const endOfYear = moment().month(10).date(7) // November 7th
        const timeDiff = endOfYear.diff(today, 'days')
        return Math.ceil(timeDiff)
    })

    onMounted(() => {

        viewStore.pageNavLoad('Gifting & Engagement', navItems.value)

        engagementStore.getEngagementGiftRequests()
            .then(response => {
                engagementStore.hydrateGiftRequests(response.data)
            })

        staffStore.getAllStaffs()
            .then(response => {
                staffStore.hydrateAllStaffs(response.data)
            })

    })

    watch(user, (newUser) => {
        if (newUser && newUser.user_id) {
            engagementStore.getEngagementOpportunities(newUser.user_id).then(response => {
                engagementStore.hydrateOpportunities(response.data)
            })
        }
    }, { immediate: true })

    onUnmounted(() => {
        viewStore.pageNavUnload()
    })

    function handleCreateEngagementGiftRequest(engagementGiftRequestData) {
        console.log('Handling Create Engagement Gift Request in Page:', engagementGiftRequestData)
        const transformedData = transformStoreEngagementRequestData(engagementGiftRequestData)
        console.log('Transformed Engagement Gift Request Data:', transformedData)
        engagementStore.storeEngagementGiftRequest(transformedData)
            .then(response => {
                // console.log('Engagement Gift Request created successfully:', response.data)
                engagementStore.addEngagementGiftRequestToLocalStore(response.data)
                notificationStore.add(`Saved New Gift Request successfully`)
            })
            .catch(error => {
                console.error('Error creating Engagement Gift Request:', error)
                notificationStore.add('Error Saving New Gift Request', 'error', 'Please try again')
            })
    }

    function handleDeleteEngagementGiftRequest(requestId) {
        engagementStore.deleteEngagementGiftRequest(requestId)
            .then(() => {
                engagementStore.removeEngagementGiftRequestFromLocalStore(requestId)
                notificationStore.add(`Deleted Gift Request successfully`)
            })
            .catch(error => {
                console.error('Error deleting Engagement Gift Request:', error)
                notificationStore.add('Error Deleting Gift Request', 'error', 'Please try again')
            })
    }

    function handleUpdateEngagementGiftRequest(requestId, engagementGiftRequestData) {
        const transformedData = transformUpdateEngagementRequestData(engagementGiftRequestData)
        engagementStore.updateEngagementGiftRequest(requestId, transformedData)
            .then(response => {
                engagementStore.updateEngagementGiftRequestInLocalStore(requestId, response.data)
                notificationStore.add(`Saved Updated Gift Request successfully`)
            })
            .catch(error => {
                console.error('Error updating Engagement Gift Request:', error)
                notificationStore.add('Error Updating Gift Request', 'error', 'Please try again')
            })
    }

    function transformStoreEngagementRequestData(data) {
        return {
            ...data,
            from: data.from.map(staff => staff.id),
            state: data.state.id,
            recipient_id: data.recipient_id,
            recipient_type: data.recipient_type,
            ...(data.recipient_type === 'contact' && {
                recipient_parent_id: data.recipient_parent_id,
                recipient_parent_type: data.recipient_parent_type,
            })
        }
    }

    // Add Manually section
    const showAddManuallySection = ref(false)
    watch(lastNavSelected, (newVal) => {
        if (newVal && newVal.id === 'cta-engagement-add-manually') {
            showAddManuallySection.value = true
        }
    }, { immediate: true })

    function handleAddManuallySelectFinished(user) {
        engagementStore.selectQuickSearchResult(user)
        showAddManuallySection.value = false
    }
    function transformUpdateEngagementRequestData(data) {
        return {
            ...data,
            ...(data.from && { from: data.from.map(staff => staff.id) }),
            ...(data.state && { state: data.state.id }),
        }
    }


    // Create and Edit Modal
    const showCreateEditModal = computed(() => {
        return selectedGiftRequest.value !== null || selectedGiftRequestLoading.value;
    })
</script>

<template>

    <div>

        <div class="mx-auto md:px-8">
            <div class="mx-auto">

                <div class="mx-auto max-w-5xl -mb-4 mt-8">

                    <div class="px-4 grid sm:grid-cols-1 gap-4 md:grid-cols-12 mb-10">

                        <div class="sm:col-span-1 pt-4 pb-8 md:py-0 md:col-span-7 md:pr-12">
                            <h3 class="font-bold mb-2 text-lifeworx-blue-700">
                                Gifting &amp; Engagement
                            </h3>
                            <p class="text-md md:text-sm tracking-wide">
                                Regular engagement strengthens relationships and demonstrates our appreciation. Thoughtful and timely gifts can show our clients and COIs feel valued and recognized in our community.
                            </p>
                        </div>

                        <div class="sm:col-span-1 md:col-span-5 md:-mt-4">
                            <h5 class="text-stone-500 font-semibold text-md md:text-xs tracking-wide text-center mb-2 md:mb-0">
                                Your Engagement Summary
                            </h5>
                            <div class="grid grid-cols-2 gap-2 md:mx-auto md:gap-4">

                                <div @click="util.scrollToElement('latte-engagement-history')"
                                    class="flex flex-col text-center text-xs font-semibold text-lifeworx-blue-700 opacity-75 hover:opacity-100 cursor-pointer"
                                >
                                    <span class="h-16 pt-3 text-5xl font-bold text-lifeworx-green-600">
                                        {{ scheduledEngagementData.length > 0 ? scheduledEngagementData.length : '&mdash;' }}
                                    </span>
                                    <span class="text-sm text-lifeworx-green-600">
                                        Scheduled
                                    </span>
                                </div>

                                <div @click="util.scrollToElement('latte-engagement-opportunities')"
                                    class="flex flex-col text-center text-xs font-semibold text-lifeworx-blue-700 opacity-75 hover:opacity-100 cursor-pointer"
                                >
                                    <span
                                        :class="[
                                            'h-16 pt-3 text-5xl font-bold',
                                            {
                                                'text-lifeworx-green-600': daysRemaining > 7,'text-lifeworx-red-600': daysRemaining <= 7,
                                                'animate-pulse': daysRemaining <= 2,
                                            }
                                        ]">
                                        {{ daysRemaining }}
                                    </span>
                                    <span
                                        :class="[
                                            'text-sm ',
                                            {

                                                'text-lifeworx-green-600': daysRemaining > 7,'text-lifeworx-red-600': daysRemaining <= 7,
                                            }
                                        ]">
                                        Days Remaining
                                    </span>
                                </div>

                            </div>
                        </div>

                    </div>

                    <Announcement
                        theme="orange"
                        class="cursor-pointer"
                        @click="announcementClick"
                    >
                        Thanksgiving Holiday
                        <template #body>Confirm selections by November 7, 12<small>PM</small></template>
                    </Announcement>

                </div>

                <LatteStyledContainer
                    id="latte-engagement-opportunities"
                    title="Opportunities"
                    :forceSingleLineMobileLayout="true"
                    :supports="[ 'collapsible', 'widescreen' ]"
                    :centerHeaderSlot="false"
                >

                    <LatteEngagementOpportunitiesTable />

                    <template #header>
                        <button
                            @click="showAddManuallySection = true"
                            class="bg-white hover:bg-zinc-200 text-lifeworx-blue-700 font-semibold text-sm md:text-xs px-4 py-2 rounded shadow"
                        >
                            Add Manually
                            <UserPlusIcon class="size-5 inline-block ml-2 -mt-1 -mb-1" />
                        </button>
                    </template>

                </LatteStyledContainer>

                <LatteStyledContainer
                    id="latte-engagement-history"
                    title="Scheduled & Sent"
                    :forceSingleLineMobileLayout="true"
                    :supports="[ 'collapsible', 'widescreen' ]"
                >

                    <LatteEngagementHistoryTable />

                    <template #header-actions>
                        <div class="flex items-center justify-center gap-2 mr-12">
                            <div class="flex-shrink-0">
                                <h2 class="text-white text-sm md:text-xs font-semibold drop-shadow tracking-wide select-none cursor-default">
                                    Mine
                                </h2>
                            </div>

                            <LwxSwitch
                                id="client-only"
                                v-model="showAllScheduledRequests"
                                palette="green"
                            />

                            <div class="flex-shrink-0">
                                <h2 class="text-white text-sm md:text-xs font-semibold drop-shadow tracking-wide select-none cursor-default">
                                    All
                                </h2>
                            </div>
                        </div>
                    </template>

                </LatteStyledContainer>

            </div>
        </div>

        <Modal
            :show="showCreateEditModal"
            :padding-enable="false"
            max-width="4xl"
            :verticalOffset="null"
        >
            <LatteEngagementCreateEditForm
                @engagementGiftRequest:create="handleCreateEngagementGiftRequest"
                @engagementGiftRequest:update="handleUpdateEngagementGiftRequest"
                @engagementGiftRequest:delete="handleDeleteEngagementGiftRequest"
            />
        </Modal>

        <QuickSearch
            v-model="showAddManuallySection"
            scope="all"
            @select="handleAddManuallySelectFinished"
        />
    </div>

</template>
